import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
  notification,
} from "antd";
import { useTranslation } from "react-i18next";
import EndPoints from "../../../services/end-points";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import { IOrderProductBatchWithdrawal } from "../../../models/order-product/types";
import { IBatch } from "../../../models/wms/batch/response";
import {
  accountingToByUnit,
  formatQuantityByUnit,
  getQuantityPrecision,
  getQuantityStepByUnit,
} from "../../../utils/helpers/quantity";

interface Props {
  visible: boolean;
  mode: "add" | "update";
  productOrderId?: number;
  productId?: number;
  storeId?: number;
  withdrawals?: IOrderProductBatchWithdrawal[] | null;
  initialBatchId?: number;
  initialQuantity?: number;
  decimalPlaces?: number;
  factorAccountingSystem?: number;
  quantityStepByUnit?: number;
  unitName?: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const BatchAllocationModal: React.FC<Props> = ({
  visible,
  mode,
  productOrderId,
  productId,
  storeId,
  withdrawals,
  initialBatchId,
  initialQuantity,
  decimalPlaces,
  factorAccountingSystem,
  quantityStepByUnit,
  unitName,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batches, setBatches] = useState<IBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null);
  const [orderProductService] = useState(
    () => new OrderProductService(httpclient),
  );

  const linkedBatchIds = useMemo(
    () => new Set((withdrawals ?? []).map((w) => w.batchId)),
    [withdrawals],
  );
  const inputPrecision = getQuantityPrecision(
    decimalPlaces,
    factorAccountingSystem,
  );
  const inputStep = getQuantityStepByUnit(
    quantityStepByUnit,
    decimalPlaces,
    factorAccountingSystem,
  );

  const getBatchFactor = (batch?: IBatch | null) =>
    batch?.factorAccountingSystem || factorAccountingSystem;

  const getBatchAvailableByUnit = (batch?: IBatch | null) =>
    batch
      ? batch.availableQuantityByUnit ??
        (getBatchFactor(batch)
          ? accountingToByUnit(batch.availableQuantity, getBatchFactor(batch))
          : undefined) ??
        0
      : undefined;

  const formatBatchQuantity = (value: unknown, batch?: IBatch | null) =>
    `${formatQuantityByUnit(
      value,
      decimalPlaces,
      getBatchFactor(batch) ?? 1,
    )}${unitName ? ` ${unitName}` : ""}`;

  const loadBatches = async () => {
    if (!productId || !storeId) return;
    setLoadingBatches(true);
    try {
      const { data } = await EndPoints.wmsBatch.getAllBatches({
        productId,
        storeId,
        hasStock: mode === "add" ? true : undefined,
        perPage: -1,
      });
      setBatches(data?.data ?? []);
    } catch (error: any) {
      setBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadSelectedBatch = async () => {
    if (!initialBatchId) return;
    setLoadingBatches(true);
    try {
      const { data } = await EndPoints.wmsBatch.getBatch(initialBatchId);
      setSelectedBatch(data ?? null);
    } catch (error: any) {
      setSelectedBatch(null);
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setSelectedBatch(null);
    if (mode === "add") {
      loadBatches();
    } else {
      form.setFieldsValue({
        batchId: initialBatchId,
        quantity: initialQuantity,
      });
      loadSelectedBatch();
    }
  }, [visible, productId, storeId, mode, initialBatchId, initialQuantity]);

  const batchOptions = useMemo(() => {
    const eligibleBatches =
      mode === "update"
        ? batches.filter((b) => linkedBatchIds.has(b.id))
        : batches;

    return eligibleBatches.map((b) => ({
      value: b.id,
      label: b.expirationDate
        ? `${b.batchNumber ?? "#" + b.id} - ${b.expirationDate} (${t("available")}: ${formatBatchQuantity(getBatchAvailableByUnit(b), b)})`
        : `${b.batchNumber ?? "#" + b.id} (${t("available")}: ${formatBatchQuantity(getBatchAvailableByUnit(b), b)})`,
    }));
  }, [batches, mode, linkedBatchIds, t, decimalPlaces, factorAccountingSystem, unitName]);

  const handleSubmit = async () => {
    if (!productOrderId) return;
    try {
      const values = await form.validateFields();
      const quantity = Number(values.quantity);

      if (mode === "add" && quantity <= 0) {
        notification.error({
          message: t("validation_error"),
          description: t("batch_add_quantity_must_be_positive"),
        });
        return;
      }

      if (mode === "update" && quantity < 0) {
        notification.error({
          message: t("validation_error"),
          description: t("batch_update_quantity_must_be_non_negative"),
        });
        return;
      }

      const batchId = mode === "update" ? initialBatchId : values.batchId;

      if (!batchId) {
        notification.error({
          message: t("validation_error"),
          description: t("please_select_batch"),
        });
        return;
      }

      if (mode === "update" && !linkedBatchIds.has(batchId)) {
        notification.error({
          message: t("validation_error"),
          description: t("batch_not_linked_to_order_line"),
        });
        return;
      }

      setLoading(true);
      await orderProductService.updateBatchAllocation({
        productOrderId,
        batchId,
        targetQuantityByUnit: quantity,
      });

      notification.success({
        message: t("success"),
        description:
          mode === "add"
            ? t("batch_allocation_added_successfully")
            : t("batch_allocation_updated_successfully"),
      });
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width="min(760px, 92vw)"
      okText={
        mode === "add" ? t("add_from_batch") : t("adjust_batch_allocation")
      }
      cancelText={t("cancel")}
      confirmLoading={loading}
      okButtonProps={{ disabled: loadingBatches }}
      title={
        mode === "add"
          ? t("add_from_specific_batch")
          : t("adjust_existing_batch_allocation")
      }
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
    >
      <Spin spinning={loadingBatches}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Typography.Text type="secondary">
            {mode === "add"
              ? t("batch_quantity_product_units_hint")
              : t("batch_target_quantity_product_units_hint")}
          </Typography.Text>
          <Form form={form} layout="vertical">
            {mode === "add" ? (
              <Form.Item
                label={t("batch")}
                name="batchId"
                rules={[{ required: true, message: t("please_select_batch") }]}
              >
                <Select
                  showSearch
                  loading={loadingBatches}
                  options={batchOptions}
                  placeholder={t("select_batch")}
                  optionFilterProp="label"
                  onChange={(batchId) => {
                    setSelectedBatch(
                      batches.find((batch) => batch.id === batchId) ?? null,
                    );
                  }}
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item name="batchId" hidden>
                  <InputNumber />
                </Form.Item>
                <Form.Item label={t("batch")}>
                  <Typography.Text>
                    {selectedBatch?.batchNumber ??
                      (initialBatchId ? `#${initialBatchId}` : "-")}
                    {selectedBatch?.expirationDate
                      ? ` - ${selectedBatch.expirationDate}`
                      : ""}
                  </Typography.Text>
                </Form.Item>
                <Space size="large" wrap>
                  <Typography.Text type="secondary">
                    {t("entered_quantity")}:{" "}
                    {formatBatchQuantity(
                      selectedBatch?.enteredQuantityByUnit,
                      selectedBatch,
                    )}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {t("withdrawn_quantity")}:{" "}
                    {formatBatchQuantity(
                      selectedBatch?.withdrawnQuantityByUnit ??
                        initialQuantity,
                      selectedBatch,
                    )}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {t("available_quantity")}:{" "}
                    {formatBatchQuantity(
                      getBatchAvailableByUnit(selectedBatch),
                      selectedBatch,
                    )}
                  </Typography.Text>
                </Space>
              </>
            )}
            <Form.Item
              label={t("quantity")}
              name="quantity"
              rules={[{ required: true, message: t("please_enter_quantity") }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={mode === "add" ? inputStep : 0}
                step={inputStep}
                precision={inputPrecision}
                stringMode
                placeholder={t("enter_quantity_in_product_units")}
              />
            </Form.Item>
          </Form>
        </Space>
      </Spin>
    </Modal>
  );
};

export default BatchAllocationModal;
